import { FC, useState, useEffect, useContext } from "react";
import PatientNote from "../../types/PatientNote";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { css } from "@emotion/css";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { blueGrey } from "@mui/material/colors";
import moment from "moment";
import NoteCreate, { noteSubmitButtonSx } from "./note-create";
import { useMutation, useQueryClient } from "react-query";
import { patchPatientNote } from "../../api/endpoints";
import APIContext from "../../contexts/APIContext";

const styles = {
  noteCard: (inDrawer: boolean) => css`
    border-radius: 0.5rem;
    padding: 1rem;
    background-color: ${inDrawer ? "transparent" : blueGrey[50]};
  `,
  noteCardHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  noteCardBody: css`
    display: flex;
    width: 100%;
    align-items: center;
    padding: 0.8rem 0;
  `,
  noteCardFooter: css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
  `,
};

const sxStyles = {
  noteCardAuthor: {
    fontWeight: "600",
    fontSize: "0.875rem",
    color: blueGrey[800],
  },
  noteCardAuthorEmail: {
    fontSize: "0.75rem",
  },
  justNowChip: {
    backgroundColor: blueGrey[600],
    color: "white",
    fontSize: "0.812rem",
  },
};

interface NoteCardProps {
  note: PatientNote;
  inDrawer?: boolean;
  onNoteDeleted?: (id: number) => void;
  readOnly?: boolean;
}

const NoteCard: FC<NoteCardProps> = ({
  note,
  inDrawer,
  onNoteDeleted,
  readOnly,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [patientNote, setPatientNote] = useState<PatientNote | null>(null);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { userPreferencesQuery } = useContext(APIContext);

  const { mutate: updateNote } = useMutation(patchPatientNote, {
    onSuccess: () => {
      queryClient.invalidateQueries("notes");
    },
  });

  const handleEditNote = (text: string) => {
    setPatientNote({ ...patientNote, content: text });
    updateNote({ id: patientNote.id, content: text });
    setIsEditing(false);
  };

  const handleDeleteNote = () => {
    updateNote({ id: patientNote.id, is_deleted: true });
    if (onNoteDeleted) {
      onNoteDeleted(patientNote.id);
    }
  };

  useEffect(() => {
    setPatientNote(note);
  }, [note]);

  useEffect(() => {
    if (userPreferencesQuery.data) {
      setCurrentUser(userPreferencesQuery.data[0].emr_user_id);
    }
  }, [userPreferencesQuery.data]);

  const timestampComponent = () => {
    const diffInSeconds = moment().diff(
      moment(patientNote?.qv_created_ts),
      "seconds",
    );
    const isWithinLastMinute = diffInSeconds < 60;
    if (isWithinLastMinute) {
      return <Chip label="Just now" size="small" sx={sxStyles.justNowChip} />;
    }

    return (
      <Typography variant="caption">
        {moment(patientNote?.qv_created_ts).fromNow()}
      </Typography>
    );
  };

  return (
    <Box className={styles.noteCard(inDrawer)}>
      <Box className={styles.noteCardHeader}>
        <Box>
          <Typography variant="h6" sx={sxStyles.noteCardAuthor}>
            {patientNote?.created_by_name}
          </Typography>
          <Typography variant="caption" sx={sxStyles.noteCardAuthorEmail}>
            {patientNote?.created_by_email}
          </Typography>
        </Box>
        <Typography variant="caption">{timestampComponent()}</Typography>
      </Box>
      <Box className={styles.noteCardBody}>
        {isEditing ? (
          <NoteCreate
            noteText={patientNote.content}
            button={
              <Button
                variant="contained"
                sx={noteSubmitButtonSx}
                aria-label="Save note"
              >
                +
              </Button>
            }
            onSubmit={handleEditNote}
          />
        ) : (
          <Typography variant="body2">{patientNote?.content}</Typography>
        )}
      </Box>
      {!isEditing && !readOnly && (
        <Box className={styles.noteCardFooter}>
          <IconButton
            disabled={currentUser !== patientNote?.created_by}
            size="small"
            aria-label="edit"
            onClick={() => setIsEditing(true)}
          >
            <ModeEditOutlineOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            disabled={currentUser !== patientNote?.created_by}
            size="small"
            aria-label="delete"
            onClick={handleDeleteNote}
          >
            <DeleteOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default NoteCard;
